import { FC, useEffect, useMemo, useState } from 'react';
import config from '../../config';
import {
  useAdminReferrals,
  useAdminReferralStats,
  usePilotSeminars,
} from '../../hooks/referralhooks';
import { buildJointSeminarUrl, REFERRAL_CHANNEL_LABELS } from '../../utils/fundingPartners';
import { downloadReferralCsv } from '../../utils/referralExport';
import { FundingPipelineCampaign } from '../FundingPipelineCampaign';

const statCardClass = 'min-w-[140px] flex-1 rounded-2xl border border-[#E4EEF1] bg-white px-4 py-3';
/** Show at most this many seminars before paging the list. */
export const SEMINAR_PAGE_SIZE = 6;

type PilotScorecardProps = {
  communityId?: string
  resourceId?: string
  partnerSlug?: string
};

/** Day-90 AiriWheels pilot scorecard scoped to a funding resource. */
export const PilotScorecard: FC<PilotScorecardProps> = ({
  communityId,
  resourceId,
  partnerSlug = 'airiwheels',
}) => {
  const [title, setTitle] = useState('HomeCook × AiriWheels seminar');
  const [date, setDate] = useState('');
  const [selectedSeminarId, setSelectedSeminarId] = useState('');
  const [seminarPage, setSeminarPage] = useState(0);
  const [pendingDeleteId, setPendingDeleteId] = useState('');
  const filters = {
    community_id: communityId,
    resource_id: resourceId,
    partner_slug: partnerSlug,
  };
  const { stats } = useAdminReferralStats(filters);
  const { referrals } = useAdminReferrals(filters);
  const {
    seminars,
    createSeminar,
    isCreatingSeminar,
    deleteSeminar,
    isDeletingSeminar,
  } = usePilotSeminars(filters);

  const pageCount = Math.max(1, Math.ceil(seminars.length / SEMINAR_PAGE_SIZE));
  const pagedSeminars = useMemo(() => {
    const start = seminarPage * SEMINAR_PAGE_SIZE;
    return seminars.slice(start, start + SEMINAR_PAGE_SIZE);
  }, [seminarPage, seminars]);

  useEffect(() => {
    if (seminarPage > pageCount - 1) {
      setSeminarPage(Math.max(0, pageCount - 1));
    }
  }, [pageCount, seminarPage]);

  const progress = (value: number | undefined, target: number | undefined) => {
    if (!target) return 0;
    return Math.min(100, Math.round(((value || 0) / target) * 100));
  };

  const handleCreateSeminar = async () => {
    await createSeminar({
      title,
      date,
      partner_slug: partnerSlug,
      community_id: communityId,
      resource_id: resourceId,
      attendee_target: 40,
    });
    setTitle('HomeCook × AiriWheels seminar');
    setDate('');
    setSeminarPage(0);
  };

  const handleDeleteSeminar = async (seminarId: string) => {
    await deleteSeminar(seminarId);
    setPendingDeleteId('');
    if (selectedSeminarId === seminarId) {
      setSelectedSeminarId('');
    }
  };

  const cards = [
    { label: 'Qualified referrals', value: stats?.qualified || 0, target: stats?.targets?.qualified || 10 },
    { label: 'Discovery', value: stats?.discovery || 0, target: stats?.targets?.discovery || 6 },
    { label: 'Activations', value: stats?.activated || 0, target: stats?.targets?.activations || 3 },
    { label: 'Fit rate', value: `${stats?.fit_rate || 0}%`, targetLabel: `${stats?.targets?.fit_rate || 70}%` },
    { label: 'Seminars', value: stats?.seminars || 0, target: stats?.targets?.seminars || 2 },
    { label: 'Attendees', value: stats?.seminar_attendees || 0, target: stats?.targets?.attendees || 40 },
  ];

  return (
    <section className="page-card mb-6 p-6 relative overflow-hidden" data-testid="pilot-scorecard">
      <p className="page-kicker">Day-90 review</p>
      <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-raleway text-xl font-bold text-ink">AiriWheels pilot scorecard</h2>
          <p className="text-sm text-muted font-open-sans mt-1">
            Targets: 8 incubators, 2 seminars / 40 attendees, 10 qualified / 6 discovery, 3 activations, 70% fit.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <FundingPipelineCampaign resourceId={resourceId} partnerSlug={partnerSlug} />
          <button
            type="button"
            className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-5 font-raleway text-sm font-semibold text-white"
            onClick={() => downloadReferralCsv(referrals)}
          >
            Export CSV
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        {cards.map((card) => (
          <div key={card.label} className={statCardClass}>
            <p className="text-xs font-raleway font-semibold uppercase tracking-wide text-[#737373]">{card.label}</p>
            <p className="mt-1 text-xl font-light font-open-sans text-ink">{card.value}</p>
            {'target' in card && card.target ? (
              <div className="mt-2 h-1.5 rounded-full bg-primary-tint">
                <div className="h-1.5 rounded-full bg-primary-400" style={{ width: `${progress(Number(card.value), card.target)}%` }} />
              </div>
            ) : (
              <p className="text-xs text-muted mt-1">Target {card.targetLabel}</p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <p className="page-kicker mb-2">By channel</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats?.by_channel || {}).map(([channel, count]) => (
            <span key={channel} className="rounded-full bg-primary-tint px-3 py-1 text-xs font-raleway font-semibold text-ink">
              {REFERRAL_CHANNEL_LABELS[channel as keyof typeof REFERRAL_CHANNEL_LABELS] || channel}: {count}
            </span>
          ))}
          {Object.keys(stats?.by_channel || {}).length === 0 && (
            <span className="text-sm text-muted font-open-sans">No channel data yet.</span>
          )}
        </div>
      </div>
      <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr]">
        <div>
          <p className="page-kicker mb-2">Register a seminar</p>
          <div className="flex flex-wrap gap-2">
            <input
              className="auth-input max-w-xs"
              placeholder="Seminar title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <input className="auth-input max-w-[180px]" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <button
              type="button"
              disabled={isCreatingSeminar || !title.trim() || !date}
              className="inline-flex h-12 items-center rounded-lg border border-primary-400 px-4 font-raleway text-sm font-semibold text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleCreateSeminar}
            >
              {isCreatingSeminar ? 'Adding…' : 'Add seminar'}
            </button>
          </div>
        </div>
        <div>
          <p className="page-kicker mb-2">Seminars</p>
          {seminars.length === 0 ? (
            <p className="text-sm text-muted">None registered yet.</p>
          ) : (
            <>
              <ul className="text-sm font-open-sans text-ink space-y-1" data-testid="seminar-list">
                {pagedSeminars.map((seminar) => {
                  const isOpen = selectedSeminarId === seminar.id;
                  const confirmingDelete = pendingDeleteId === seminar.id;
                  return (
                    <li key={seminar.id} className="rounded-lg border border-transparent hover:border-[#E4EEF1]">
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 rounded-lg px-2 py-1 text-left hover:bg-primary-tint focus:outline-none focus:ring-2 focus:ring-primary-400"
                          aria-expanded={isOpen}
                          aria-controls={`seminar-details-${seminar.id}`}
                          onClick={() => setSelectedSeminarId(isOpen ? '' : (seminar.id || ''))}
                        >
                          {seminar.title} {seminar.date ? `· ${seminar.date}` : ''}
                        </button>
                        {confirmingDelete ? (
                          <div className="flex shrink-0 items-center gap-1 pr-1 pt-1">
                            <button
                              type="button"
                              className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                              disabled={isDeletingSeminar}
                              onClick={() => handleDeleteSeminar(seminar.id || '')}
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-[#E4EEF1] px-2 py-1 text-xs text-muted"
                              disabled={isDeletingSeminar}
                              onClick={() => setPendingDeleteId('')}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-[#808080] hover:bg-[#F7FAFB] hover:text-red-600"
                            aria-label={`Delete seminar ${seminar.title || seminar.id}`}
                            title="Delete seminar"
                            onClick={() => setPendingDeleteId(seminar.id || '')}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <div
                          id={`seminar-details-${seminar.id}`}
                          data-testid="seminar-details"
                          className="mt-1 rounded-lg border border-[#E4EEF1] bg-white px-3 py-2 text-xs text-muted space-y-0.5"
                        >
                          <p>Attendees: {seminar.attendee_count || 0} of {seminar.attendee_target || 0} target</p>
                          <p>Incubators: {seminar.incubator_count || 0}</p>
                          <p>Rating: {seminar.rating ? `${seminar.rating} / 5` : 'Not rated yet'}</p>
                          {(seminar.landing_url || (resourceId && seminar.id)) && (
                            <a
                              className="text-primary-300 underline break-all"
                              href={seminar.landing_url || buildJointSeminarUrl(config.webUrl, resourceId || '', seminar.id || '')}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open Resource Pantry page
                            </a>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {seminars.length > SEMINAR_PAGE_SIZE && (
                <div className="mt-3 flex items-center justify-between gap-2" data-testid="seminar-pagination">
                  <button
                    type="button"
                    className="rounded-md border border-[#E4EEF1] px-3 py-1 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={seminarPage === 0}
                    onClick={() => setSeminarPage((page) => Math.max(0, page - 1))}
                  >
                    Previous
                  </button>
                  <p className="text-xs text-muted">
                    Page {seminarPage + 1} of {pageCount}
                  </p>
                  <button
                    type="button"
                    className="rounded-md border border-[#E4EEF1] px-3 py-1 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={seminarPage >= pageCount - 1}
                    onClick={() => setSeminarPage((page) => Math.min(pageCount - 1, page + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};
